import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { projects as fallback } from "@/data/portfolio";

const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.07, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Projects() {
  const [items, setItems] = useState(fallback);

  useEffect(() => {
    let active = true;
    api
      .get("/projects")
      .then(({ data }) => {
        if (active && Array.isArray(data) && data.length) setItems(data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  return (
    <section
      id="work"
      data-testid="projects-section"
      className="relative py-16 md:py-20 lg:py-24 bg-black"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 text-sm text-zinc-500"
        >
          <span className="w-8 h-px bg-zinc-700" />
          <span>01 · Selected work</span>
        </motion.div>

        <div className="mt-5 flex flex-col md:flex-row md:items-end justify-between gap-5">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading text-3xl md:text-5xl lg:text-6xl font-light tracking-[-0.025em] leading-[1.05] text-white max-w-3xl"
          >
            Things I&apos;ve built when{" "}
            <span className="text-zinc-500">no one assigned them.</span>
          </motion.h2>
          <p className="text-sm text-zinc-500 max-w-xs">
            A non-exhaustive index of robots, boards, and ideas. Click any card
            for the full story.
          </p>
        </div>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
          {items.map((p, i) => (
            <motion.div
              key={p.id || p.title}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              className="h-full"
            >
              <Link
                to={`/work/${p.id}`}
                data-testid={`project-card-${p.id || i}`}
                className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] hover:border-white/25 transition-colors duration-500"
              >
                <div className="relative aspect-[16/11] overflow-hidden bg-black">
                  {p.image && (
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover grayscale-[60%] group-hover:grayscale-0 scale-[1.02] group-hover:scale-105 transition-all duration-[1200ms] ease-out"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  <div className="absolute top-5 left-5 right-5 flex items-center justify-between text-sm">
                    <span className="text-white/80 bg-black/40 border border-white/10 backdrop-blur-md px-3 py-1 rounded-full">
                      {p.tag}
                    </span>
                    <span className="text-zinc-400">{p.year}</span>
                  </div>
                </div>

                <div className="flex flex-1 flex-col p-7 md:p-8">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-heading text-xl md:text-2xl font-medium tracking-tight text-white">
                      {p.title}
                    </h3>
                    <div className="shrink-0 w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white group-hover:bg-white group-hover:text-black transition-colors duration-300">
                      <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                    </div>
                  </div>
                  <p className="mt-3 text-sm md:text-base leading-relaxed text-zinc-400 font-light">
                    {p.summary}
                  </p>
                  <ul className="mt-auto pt-5 flex flex-wrap gap-2">
                    {(p.stack || []).map((s) => (
                      <li
                        key={s}
                        className="text-xs text-zinc-400 bg-white/[0.03] border border-white/10 px-2.5 py-1 rounded-full"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
